import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography, LinearProgress, Tabs, Tab, CircularProgress } from "@mui/material";
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from "@mui/lab";
import { Calendar, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const ProjectTimeline = () => {
  const [projects, setProjects] = useState({});
  const [projectsList, setProjectsList] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Charger les projets depuis Firestore
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const projectsSnapshot = await getDocs(collection(db, 'projects'));
        const projectsData = {};
        const projectsArray = [];
        const orderedProjects = [];
        
        for (const projectDoc of projectsSnapshot.docs) {
          const projectData = projectDoc.data();
          const stepsSnapshot = await getDocs(collection(db, 'projects', projectDoc.id, 'steps'));
          
          const steps = stepsSnapshot.docs.map(stepDoc => ({
            id: stepDoc.id,
            ...stepDoc.data()
          }));
          
          const projectWithSteps = {
            id: projectDoc.id,
            title: projectData.title,
            details: projectData.details || {},
            order: projectData.order || 999, // Valeur par défaut élevée pour les projets sans ordre
            steps: steps
          };
          
          orderedProjects.push(projectWithSteps);
        }
        
        // Trier les projets par ordre croissant
        orderedProjects.sort((a, b) => a.order - b.order);
        
        // Créer les objets de données après le tri
        orderedProjects.forEach(project => {
          projectsData[project.title] = project;
          projectsArray.push(project.title);
        });
        
        setProjects(projectsData);
        setProjectsList(projectsArray);
        
        // Sélectionner le premier projet par défaut s'il y en a
        if (projectsArray.length > 0) {
          setSelectedProject(projectsArray[0]);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des projets:', err);
        setError('Impossible de charger les projets. ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, []);
  
  // Si aucun projet n'est sélectionné ou si les données sont en cours de chargement
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress sx={{ color: '#014527' }} />
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
      </div>
    );
  }
  
  if (projectsList.length === 0) {
    return (
      <div 
        id="projects"
        style={{ 
          maxWidth: "100%",
          backgroundColor: "#014527",
          color: "white",
        }}
      >
        <h2 style={{
          textAlign:'center'
        }}>Nos Projets :</h2>
        
        <div style={{ padding: '40px', textAlign: 'center', margin: '0 auto', maxWidth: '800px' }}>
          <h3 style={{ color: 'white', marginBottom: '20px', fontSize: '24px' }}>Nos projets sont en cours de développement</h3>
          <p style={{ fontSize: '16px', lineHeight: '1.6', color: 'white', marginBottom: '20px' }}>
            Chez Agri Vision Gabon, nous travaillons actuellement sur plusieurs projets innovants visant à transformer le secteur agricole gabonais. 
            Notre équipe d'experts est en phase d'étude et de planification pour garantir des initiatives durables et à fort impact.
          </p>
          <p style={{ fontSize: '16px', lineHeight: '1.6', color: 'white', marginBottom: '20px' }}>
            Nous finalisons les analyses de faisabilité, les études de marché et les partenariats stratégiques 
            nécessaires pour assurer le succès de nos futurs projets. Nous vous invitons à revenir prochainement 
            pour découvrir nos initiatives qui contribueront à la souveraineté alimentaire du Gabon.
          </p>
          <p style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
            Merci de votre intérêt pour notre vision. Nous avons hâte de partager nos projets avec vous très bientôt.
          </p>
        </div>
      </div>
    );
  }
  
  const { details = {}, steps = [] } = projects[selectedProject] || {};
  const overallProgress = steps.length > 0 
    ? steps.reduce((acc, step) => acc + (parseFloat(step.progress) || 0), 0) / steps.length 
    : 0;

  return (
    <div 
        id="projects"
        style={{ 
          maxWidth: "100%", // Assurez-vous que la largeur est 100% pour les petits écrans
          // margin: "20px auto",
          // margin: 20,
          backgroundColor: "#014527",
          color: "white",
        }}
      >
      <h2 style={{
        textAlign:'center'
      }}>Nos Projets :</h2>
      <Tabs 
        value={selectedProject} 
        onChange={(e, newValue) => setSelectedProject(newValue)} 
        variant="scrollable" // Permet le défilement si trop d'onglets
        scrollButtons="auto" // Affiche les boutons de défilement si nécessaire
        sx={{
          '.MuiTabs-indicator': {
            backgroundColor: 'white',
          },
          '.Mui-selected': {
            color: 'white !important',
          },
          '@media (max-width: 600px)': { // Appliquer en mode mobile
            fontSize: "12px",  // Réduit la taille de la police
            minWidth: "auto", // Évite que les onglets prennent trop d'espace
          }
        }}
      >
        {projectsList.map((project) => (
          <Tab 
            key={project} 
            label={project} 
            value={project} 
            sx={{
              '@media (max-width: 600px)': {
                fontSize: "12px",  // Ajuster la taille de la police des onglets
                padding: "4px 8px" // Réduire l'espacement
              }
            }}
          />
        ))}
      </Tabs>

      {/* Carte de progression globale */}
      <Card sx={{ 
        width: '80%',
        margin: '5px auto', // Utiliser un espace réduit pour mobile
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        backgroundColor:'azure'
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ 
            textAlign: 'center',
            color: '#1e293b',
            fontWeight: '600',
            marginBottom: 1
          }} >
            {selectedProject}
          </Typography>
          <Typography variant="h8" sx={{ 
            textAlign: 'center',
            color: '#475569',
            marginBottom: 1,
            '@media (max-width: 600px)': {
                  marginTop: 1,
                  fontSize: '15px',
                }
          }} >
            Progression globale : {overallProgress.toFixed(1)}%
          </Typography>
          <div style={{ padding: '0 16px' }}>
            <LinearProgress 
              variant="determinate" 
              value={overallProgress} 
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e2e8f0',
                '.MuiLinearProgress-bar': {
                  backgroundColor: '#16a34a'
                }
              }}
            />
          </div>
          <Typography variant="body1" sx={{ 
            textAlign: 'center',
            marginTop: 2,
            color: '#475569',
            
            '@media (max-width: 600px)': {
                  marginTop: 1,
                  fontSize: '15px',
                }
          }} >
            Progression globale du projet
          </Typography>
        </CardContent>
      </Card>

      {/* Contenu principal */}
      <div style={{ 
        margin: "20px auto",
        maxWidth: "1224px",
        display: "flex",
        flexDirection: "column", // En mobile, empilé verticalement
        gap: "16px",
        padding: '0 8px',
        '@media (min-width: 1024px)': { // Pour desktop
          flexDirection: "row", // En desktop, côte à côte
          gap: "24px",
        }
      }}>

        {/* Timeline */}
        <div style={{ 
          width: "100%",
          '@media (min-width: 1024px)': {
            width: "800px" // Largeur fixe en desktop
          }
        }}>
          <Timeline sx={{
            [`& .MuiTimelineItem-root:before`]: {
              flex: 0,
              padding: 0
            },
            '@media screen and (max-width: 480px)': { // Pour des écrans encore plus petits
              padding: "0", // Enlever tout padding inutile
            }
          }}>
            {steps.map((step, index) => (
              <TimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot sx={{
                    backgroundColor: step.status === "terminé" ? "#16a34a" : 
                                   step.status === "en-cours" ? "#2563eb" : "#94a3b8",
                    boxShadow: "0 0 0 4px rgba(0, 0, 0, 0.05)"
                  }} />
                  {index < steps.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Card sx={{ 
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                    '&:hover': {
                      boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                      transform: "translateY(-2px)",
                      transition: "all 0.2s ease"
                    },
                    position: "relative",
                    overflow: "visible", // Pour permettre aux éléments de déborder de la carte
                    borderLeft: `4px solid ${step.status === "terminé" ? "#16a34a" : 
                                          step.status === "en-cours" ? "#2563eb" : 
                                          "#94a3b8"}`
                  }}>
                    {/* Badge de statut */}
                    <div style={{
                      position: "absolute",
                      top: "-12px",
                      right: "16px",
                      backgroundColor: step.status === "terminé" ? "#16a34a" : 
                                     step.status === "en-cours" ? "#2563eb" : 
                                     "#94a3b8",
                      color: "white",
                      borderRadius: "20px",
                      padding: "4px 12px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      zIndex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      {step.status === "terminé" ? (
                        <CheckCircle size={14} />
                      ) : step.status === "en-cours" ? (
                        <Clock size={14} />
                      ) : (
                        <Calendar size={14} />
                      )}
                      {step.status}
                    </div>
                    
                    {/* Badge de progression */}
                    <div style={{
                      position: "absolute",
                      bottom: "-10px",
                      right: "16px",
                      backgroundColor: "white",
                      color: step.progress >= 100 ? "#16a34a" : 
                             step.progress >= 50 ? "#f59e0b" : 
                             "#64748b",
                      borderRadius: "20px",
                      padding: "4px 12px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      border: `2px solid ${step.progress >= 100 ? "#16a34a" : 
                                          step.progress >= 50 ? "#f59e0b" : 
                                          "#64748b"}`,
                      zIndex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      <TrendingUp size={14} />
                      {step.progress.toFixed(0)}% complété
                    </div>
                    
                    <CardContent sx={{ pt: 3, pb: 4 }}>
                      <div className="flex flex-col mb-4">
                        <Typography variant="h6" sx={{ 
                          fontWeight: "600",
                          color: "#1e293b",
                          mb: 1
                        }}>
                          {`Étape ${index + 1}: ${step.title}`}
                        </Typography>
                        
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar size={16} className="text-gray-500" />
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: "500" }}>
                            {step.date}
                          </Typography>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4 bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <Typography variant="body2" sx={{ fontWeight: "600", color: "#475569" }}>
                            Progression de l'étape
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            fontWeight: "600", 
                            color: step.progress >= 100 ? "#16a34a" : 
                                  step.progress >= 50 ? "#f59e0b" : 
                                  "#64748b" 
                          }}>
                            {step.progress}%
                          </Typography>
                        </div>
                        <LinearProgress 
                          variant="determinate" 
                          value={step.progress} 
                          sx={{ 
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e2e8f0',
                            '.MuiLinearProgress-bar': {
                              backgroundColor: step.progress >= 100 ? "#16a34a" : 
                                             step.progress >= 50 ? "#f59e0b" : 
                                             "#3b82f6"
                            }
                          }}
                        />
                      </div>

                      {step.substeps && step.substeps.length > 0 && (
                        <div className="mt-4 bg-white p-2 rounded-lg border border-gray-200">
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: '600', 
                            mb: 1.5,
                            color: '#334155',
                            fontSize: '0.875rem',
                            borderBottom: '1px solid #e2e8f0',
                            paddingBottom: '8px'
                          }}>
                            Sous-étapes :
                          </Typography>
                          
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '0.875rem'
                          }}>
                            <tbody>
                              {step.substeps.map((substep, subIdx) => {
                                const currentStatus = substep.status || 'pas commencé';
                                const statusColor = 
                                  currentStatus === 'réalisé' ? '#16a34a' : 
                                  currentStatus === 'en cours' ? '#2563eb' : 
                                  '#64748b';
                                
                                return (
                                  <tr 
                                    key={substep.id || subIdx}
                                    style={{
                                      borderBottom: subIdx < step.substeps.length - 1 ? '1px solid #f1f5f9' : 'none'
                                    }}
                                  >
                                    <td style={{ 
                                      padding: '8px 4px',
                                      width: '70%',
                                      color: '#334155',
                                      fontWeight: '500'
                                    }}>
                                      {substep.title}
                                    </td>
                                    <td style={{ 
                                      padding: '8px 4px',
                                      textAlign: 'right',
                                      width: '30%'
                                    }}>
                                      <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: 'rgba(241, 245, 249, 0.7)',
                                        fontSize: '0.75rem',
                                        fontWeight: '500',
                                        color: statusColor
                                      }}>
                                        {currentStatus === 'réalisé' && (
                                          <CheckCircle size={10} />
                                        )}
                                        {currentStatus === 'en cours' && (
                                          <Clock size={10} />
                                        )}
                                        {currentStatus}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </div>
      </div>
    </div>
  );
};

export default ProjectTimeline;